// src/pages/CaseStudies.tsx
import React from "react";
import SEOHead from "../components/SEOHead";
import {
  Target, Shield, Phone, Building2, CheckCircle2, ArrowRight, MessageSquare
} from "lucide-react";
import AnimatedSection from "../components/AnimatedSection";
import PageTransition from "../components/PageTransition";
import GradientHero from "../components/GradientHero";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 }
  })
};

// JSON-LD
const jsonLdCaseStudies = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "name": "Études de cas Neoqualis",
      "url": "https://neoqualis.com/case-studies",
      "description": "Découvrez comment Neoqualis aide ses clients à fiabiliser leurs parcours critiques, harmoniser la QA et prioriser les fonctionnalités."
    },
    {
      "@type": "CreativeWork",
      "name": "Études de cas QA & Testing",
      "about": "Quality Assurance, Agile, Risk-based testing, CI/CD",
      "audience": { "@type": "Audience", "audienceType": "Direction IT, Produit, QA" },
      "creator": { "@type": "Organization", "name": "Neoqualis" }
    }
  ]
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": "https://neoqualis.com/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Études de cas",
      "item": "https://neoqualis.com/case-studies"
    }
  ]
};

const combinedJsonLd = [jsonLdCaseStudies, breadcrumbJsonLd];

const caseStudies = [
  {
    title: "Agilité — rituels non respectés",
    desc: "Une équipe projet ne respectait pas les cérémonies Agile (daily, rétros, sprints irréguliers). Résultat : perte de visibilité et dérives de planning.",
    intervention: "Coaching Agile et QA Lead pour cadrer la gouvernance, instaurer des rituels efficaces et un backlog priorisé.",
    results: ["+25% vélocité", "Planning stabilisé", "Équipe remotivée"],
    icon: Target
  },
  {
    title: "Harmonisation des cas de test",
    desc: "Les cas de test étaient dispersés (Excel/Word), sans traçabilité ni cohérence. Couverture faible et anomalies découvertes tardivement.",
    intervention: "Mise en place d'un référentiel Xray/Squash TM, templates normalisés et traçabilité exigences → tests.",
    results: ["85% couverture", "- anomalies tardives", "Vision claire"],
    icon: Shield
  },
  {
    title: "Automatisation fragile et instable",
    desc: "Une suite Cypress existait mais souffrait de flakiness. Rapports CI/CD peu fiables, mises en production bloquées.",
    intervention: "Refonte du framework (POM, data management), bonnes pratiques et intégration CI/CD.",
    results: ["-40% tests instables", "Rapports fiables", "Mises en prod fluides"],
    icon: Phone
  },
  {
    title: "Priorisation des fonctionnalités critiques",
    desc: "Toutes les fonctionnalités étaient testées au même niveau. Les parcours critiques (paiement, KYC, auth) manquaient de couverture.",
    intervention: "Risk-based testing + dashboards QA pour prioriser les fonctionnalités critiques.",
    results: ["Focus parcours critiques", "-60% d'incidents de prod", "Go/No-Go objectif"],
    icon: Building2
  }
];

const CaseStudies: React.FC = () => {
  return (
    <PageTransition>
      <SEOHead
        title="Études de cas — Neoqualis"
        description="Découvrez comment Neoqualis a renforcé la qualité logicielle de ses clients grâce à l'agilité, l'harmonisation des tests, l'automatisation fiable et la priorisation des parcours critiques."
        canonical="https://neoqualis.com/case-studies"
        jsonLd={combinedJsonLd}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Études de cas"
          subtitle="Cas concrets : agilité, QA, automation et priorisation. Découvrez comment Neoqualis transforme les pratiques de test."
          ctas={[
            { label: "Discuter de mon projet", to: "/contact?type=case-study", variant: "primary", iconRight: <MessageSquare /> },
            { label: "Demander un audit QA", to: "/audit", variant: "secondary", iconRight: <ArrowRight /> }
          ]}
        />

        {/* Cases */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Nos retours d'expérience</h2>
              <p className="subheading max-w-2xl mx-auto">
                Problématique, intervention et résultats mesurables.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {caseStudies.map(({ title, desc, intervention, results, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full flex flex-col p-8 hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700 mb-4">{desc}</p>
                    <div className="mb-4">
                      <p className="font-semibold text-gray-900 mb-2">➡ Intervention :</p>
                      <p className="text-gray-700">{intervention}</p>
                    </div>
                    <div className="mt-auto">
                      <p className="font-semibold text-gray-900 mb-2">✅ Résultats :</p>
                      <div className="flex flex-wrap gap-2">
                        {results.map((r) => (
                          <span
                            key={r}
                            className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Métriques d'impact */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Impact global de nos interventions</h2>
              <p className="subheading max-w-2xl mx-auto">
                Résultats moyens observés sur l'ensemble de nos missions
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { metric: "-50%", description: "d'anomalies en production", color: "text-green-600" },
                { metric: "+35%", description: "de vélocité équipe", color: "text-blue-600" },
                { metric: "85%", description: "de couverture fonctionnelle", color: "text-purple-600" }
              ].map(({ metric, description, color }, i) => (
                <motion.div
                  key={metric}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card text-center">
                    <div className={`text-4xl font-bold ${color} mb-2`}>{metric}</div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">Et si nous parlions de votre cas ?</h2>
                <p className="subheading mb-8">
                  Nous analysons vos pratiques QA/Testing et identifions rapidement
                  des leviers d'amélioration adaptés à vos enjeux.
                </p>
                <div className="flex flex-col gap-4 justify-center max-w-md mx-auto">
                  <Link 
                    to="/contact?type=case-study" 
                    className="btn-primary inline-flex items-center justify-center w-full"
                  >
                    Discuter de mon projet
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link 
                    to="/audit" 
                    className="btn-secondary inline-flex items-center justify-center w-full"
                  >
                    Demander un audit QA (offert – 30 min)
                    <ArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default CaseStudies;